import {Component, computed, DestroyRef, effect, OnInit, signal, WritableSignal} from '@angular/core';
import {PanelComponent} from "../../../panel/panel.component";
import {BannerSpec} from '../../../../core/models/banner-spec.model';
import {AvailableBannerType} from '../../../../core/enums/banner/available-banner-type.enum';
import {ReportFacade} from '../../facade/report.facade';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {AsyncPipe, NgClass} from '@angular/common';
import {AvailableReportColumn} from '../../model/available-report-column.model';
import {Message} from 'primeng/message';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {Step, StepList, StepPanel, StepPanels, Stepper} from 'primeng/stepper';
import {tablerCalendarClock, tablerCar, tablerKey, tablerTableColumn} from '@ng-icons/tabler-icons';
import {Button} from 'primeng/button';
import {Brand} from '../../../entities/brand/models/brand.model';
import {map} from 'rxjs';
import {Tooltip} from 'primeng/tooltip';
import {Chip} from 'primeng/chip';
import {
  MultiSelectInputComponent
} from '../../../../shared/components/input/multiselect-input/multiselect-input.component';
import {VehicleCategory} from '../../../entities/category/models/vehicle-category.model';
import {DateTime} from 'luxon';
import {AppDateInputComponent} from '../../../../shared/components/input/date-input/date-input.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Owner} from '../../../entities/owners/models/owner.model';
import {VehicleModel} from '../../../entities/model/model/vehicle-model.model';
import {ReportFormGroup, ReportFormValue} from '../../model/report-form.model';
import {VehicleStorage} from '../../../entities/storage/model/storage.model';
import {AppTextChipInputComponent} from '../../../../shared/components/input/text-chip-input/text-chip-input.component';
import {LoadingComponent} from '../../../../shared/components/loading/loading/loading.component';

export enum ReportFetchStatus {
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
}

@Component({
  selector: 'app-report',
  imports: [
    PanelComponent,
    ReactiveFormsModule,
    Message,
    NgIcon,
    Step,
    StepList,
    StepPanel,
    StepPanels,
    Stepper,
    NgClass,
    Button,
    Tooltip,
    Chip,
    MultiSelectInputComponent,
    AsyncPipe,
    FormsModule,
    AppDateInputComponent,
    AppTextChipInputComponent,
    LoadingComponent
  ],
  providers: [provideIcons({
    tablerTableColumn,
    tablerCar,
    tablerCalendarClock,
    tablerKey
  })],
  templateUrl: './report.component.html',
  styleUrl: './report.component.css',
})
export class ReportComponent implements OnInit {

  protected readonly selectedReportColumns: WritableSignal<AvailableReportColumn[]> = signal<AvailableReportColumn[]>([]);

  protected readonly isLoading = computed(() => {
    return this.reportFacade.isLoading()
  });

  protected banner: BannerSpec;

  protected activeStep: number = 1;

  protected readonly reportForm: ReportFormGroup = new FormGroup({
    reportSchemeSection: new FormGroup({
      selectedReportColumns: new FormControl<AvailableReportColumn[]>([], [Validators.nullValidator])
    }),
    vehicleDetailsSection: new FormGroup({
      categories: new FormControl<VehicleCategory[]>([]),
      brands: new FormControl<Brand[]>([]),
      models: new FormControl<VehicleModel[]>([]),
      vins: new FormControl<string[]>([]),
      registrationPlates: new FormControl<string[]>([]),
      factoryNumbers: new FormControl<string[]>([]),
      storages: new FormControl<VehicleStorage[]>([]),
      owners: new FormControl<Owner[]>([]),
      returningPersons: new FormControl<string[]>([]),
      receivers: new FormControl<string[]>([])
    }),
    vehicleTimetablesSection: new FormGroup({
      arrivalDateFrom: new FormControl<DateTime | null>(null),
      arrivalDateTo: new FormControl<DateTime | null>(null),
      releaseDateFrom: new FormControl<DateTime | null>(null),
      releaseDateTo: new FormControl<DateTime | null>(null),
    }),
    vehicleDocumentSection: new FormGroup({
      keyNumbers: new FormControl<number[]>([]),
      documentNumbers: new FormControl<number[]>([]),
      archiveNumbers: new FormControl<number[]>([])
    }),
  });

  get form(): FormGroup {
    return this.reportForm;
  }

  protected get reportSchemeSection(): FormGroup {
    return this.form.get('reportSchemeSection') as FormGroup;
  }

  protected get vehicleDetailsSection(): FormGroup {
    return this.form.get('vehicleDetailsSection') as FormGroup;
  }

  protected get vehicleTimetablesSection(): FormGroup {
    return this.form.get('vehicleTimetablesSection') as FormGroup;
  }

  protected get vehicleDocumentSection(): FormGroup {
    return this.form.get('vehicleDocumentSection') as FormGroup;
  }

  constructor(
    private readonly reportFacade: ReportFacade,
    private readonly destroyRef: DestroyRef
  ) {
    this.banner = {
      type: AvailableBannerType.INFO,
      message: "Wygeneruj raport o stanie magazynowym. " +
        "Zdefiniuj konstrukcję tabeli wykorzystając z dostępnych kategorii kolumn. " +
        "Wygenerowany raport zostanie doręczony na skrzynkę e-mail."
    }

    this.registerReportColumnsSubscription();
  }

  get reportOrchestrator(): ReportFacade {
    return this.reportFacade;
  }

  public ngOnInit(): void {
    this.reportFacade.fetchAvailableReportColumns();
    this.reportFacade.fetchVehicleCategories();
    this.reportFacade.fetchVehicleBrands();
    this.registerVehicleBrandsIds();
    this.registerArrivalDateRangeValiditySubscription();
    this.registerReturnDateRangeValiditySubscription();
  }

  protected onSubmit() {
    const formValue = this.form.value as ReportFormValue;
    this.reportFacade.fetchReport(formValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.reset();
        this.activeStep = 1;
      });
  }

  private registerReportColumnsSubscription(): void {
    effect(() => {
      const control = this.reportSchemeSection.get('selectedReportColumns');
      control?.valueChanges.subscribe(columns => {
        this.selectedReportColumns.set(columns ?? []);
      });
    });
  }

  private registerVehicleBrandsIds(): void {
    this.vehicleDetailsSection.get('brands')?.valueChanges
      .pipe(
        map((brands: Brand[] | null) => (brands || []).map(b => b.id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(brandIds => {
        this.reportFacade.fetchVehicleModelsByBrands(brandIds);
      });
  }

  private registerArrivalDateRangeValiditySubscription(): void {
    this.vehicleTimetablesSection.get('arrivalDateTo')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(arrivalDateToValue => {
        const arrivalDateFromControl = this.vehicleTimetablesSection.get('arrivalDateFrom');
        const arrivalDateFromValue = arrivalDateFromControl?.value;

        if (arrivalDateFromValue && arrivalDateToValue && arrivalDateFromValue.toMillis() > arrivalDateToValue.toMillis()) {
          arrivalDateFromControl?.setErrors({invalidDateRange: true});
        } else {
          arrivalDateFromControl?.setErrors(null);
        }
      })
  }

  private registerReturnDateRangeValiditySubscription(): void {
    this.vehicleTimetablesSection.get('releaseDateTo')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(returnDateTo => {
        const releaseDateFromControl = this.vehicleTimetablesSection.get('releaseDateFrom');
        const releaseDateFromValue = releaseDateFromControl?.value;

        if (releaseDateFromValue && returnDateTo && releaseDateFromValue.toMillis() > returnDateTo.toMillis()) {
          releaseDateFromControl?.setErrors({invalidDateRange: true});
        } else {
          releaseDateFromControl?.setErrors(null);
        }
      })
  }
}
